/**
 * ActivityStream Widget
 *
 * Real-time activity feed with WebSocket connection, pause/resume,
 * filtering, time grouping, and severity indicators.
 *
 * Phase 3.5-C: Operational Intelligence
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useActivityStream } from '../../../features/dashboard/hooks/useActivityStream';
import type {
  ActivityItem,
  ActivityType,
  ActivitySeverity,
  ConnectionStatus,
} from '../../../features/dashboard/types/activity';
import {
  ActivityIcon,
  PauseIcon,
  PlayIcon,
  FilterIcon,
  AlertIcon,
  SecurityIcon,
  ServerIcon,
  UsersIcon,
  CloudIcon,
  ShieldIcon,
  ZapIcon,
  CloseIcon,
} from '../../common/Icons';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SEVERITY_COLORS: Record<ActivitySeverity, string> = {
  info: 'rgba(96, 165, 250, 0.8)',
  success: 'rgba(74, 222, 128, 0.8)',
  warning: 'rgba(251, 191, 36, 0.8)',
  error: 'rgba(248, 113, 113, 0.8)',
  critical: 'rgba(239, 68, 68, 1)',
};

const SEVERITY_BG: Record<ActivitySeverity, string> = {
  info: 'rgba(96, 165, 250, 0.1)',
  success: 'rgba(74, 222, 128, 0.1)',
  warning: 'rgba(251, 191, 36, 0.1)',
  error: 'rgba(248, 113, 113, 0.1)',
  critical: 'rgba(239, 68, 68, 0.15)',
};

const TYPE_ICONS: Record<ActivityType, React.FC<{ size?: number }>> = {
  deployment: CloudIcon,
  user: UsersIcon,
  tenant: ServerIcon,
  container: ServerIcon,
  security: SecurityIcon,
  system: ZapIcon,
  alert: AlertIcon,
  compliance: ShieldIcon,
  upgrade: CloudIcon,
};

const STATUS_LABELS: Record<ConnectionStatus, string> = {
  connected: '● Live',
  connecting: '◌ Connecting...',
  disconnected: '○ Disconnected',
};

const STATUS_COLORS: Record<ConnectionStatus, string> = {
  connected: '#4ade80',
  connecting: '#fbbf24',
  disconnected: '#f87171',
};

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getTimeGroup(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / 3_600_000;

  if (diffHours < 1) return 'Last Hour';
  if (date.toDateString() === now.toDateString()) return 'Today';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return 'Earlier';
}

function groupByTime(items: ActivityItem[]): Map<string, ActivityItem[]> {
  const groups = new Map<string, ActivityItem[]>();
  const order = ['Last Hour', 'Today', 'Yesterday', 'Earlier'];

  for (const label of order) {
    groups.set(label, []);
  }

  for (const item of items) {
    const group = getTimeGroup(item.timestamp);
    groups.get(group)!.push(item);
  }

  // Remove empty groups
  for (const [key, value] of groups) {
    if (value.length === 0) groups.delete(key);
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ActivityItemRow: React.FC<{
  item: ActivityItem;
  onAcknowledge: (id: string) => void;
}> = ({ item, onAcknowledge }) => {
  const IconComponent = TYPE_ICONS[item.type] || ActivityIcon;

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        padding: '10px 12px',
        borderRadius: '8px',
        background: item.is_acknowledged ? 'transparent' : SEVERITY_BG[item.severity],
        opacity: item.is_acknowledged ? 0.6 : 1,
        transition: 'background 0.2s, opacity 0.2s',
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: SEVERITY_BG[item.severity],
          color: SEVERITY_COLORS[item.severity],
          flexShrink: 0,
        }}
      >
        <IconComponent size={16} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px',
          }}
        >
          <span
            style={{
              fontWeight: 500,
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              lineHeight: '18px',
            }}
          >
            {item.title}
          </span>
          <span
            style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.4)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {timeAgo(item.timestamp)}
          </span>
        </div>
        <p
          style={{
            margin: '2px 0 0',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.5)',
            lineHeight: '16px',
          }}
        >
          {item.description}
        </p>
        {(item.severity === 'warning' || item.severity === 'error' || item.severity === 'critical') &&
          !item.is_acknowledged && (
            <button
              onClick={() => onAcknowledge(item.id)}
              style={{
                marginTop: '6px',
                padding: '2px 8px',
                fontSize: '11px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '4px',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
              }}
            >
              Acknowledge
            </button>
          )}
      </div>
    </div>
  );
};

const FilterPanel: React.FC<{
  selectedTypes: Set<ActivityType>;
  selectedSeverities: Set<ActivitySeverity>;
  onToggleType: (type: ActivityType) => void;
  onToggleSeverity: (severity: ActivitySeverity) => void;
  onClose: () => void;
}> = ({ selectedTypes, selectedSeverities, onToggleType, onToggleSeverity, onClose }) => {
  const allTypes: ActivityType[] = [
    'deployment', 'user', 'tenant', 'container',
    'security', 'system', 'alert', 'compliance', 'upgrade',
  ];
  const allSeverities: ActivitySeverity[] = ['info', 'success', 'warning', 'error', 'critical'];

  const chipStyle = (active: boolean, color?: string): React.CSSProperties => ({
    padding: '3px 10px',
    fontSize: '11px',
    borderRadius: '12px',
    border: `1px solid ${active ? (color || 'rgba(96,165,250,0.5)') : 'rgba(255,255,255,0.12)'}`,
    background: active ? (color ? `${color}22` : 'rgba(96,165,250,0.1)') : 'transparent',
    color: active ? (color || 'rgba(96,165,250,0.9)') : 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        padding: '12px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Filters
        </span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}>
          <CloseIcon size={14} />
        </button>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '4px' }}>Type</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {allTypes.map((t) => (
            <button key={t} onClick={() => onToggleType(t)} style={chipStyle(selectedTypes.has(t))}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '4px' }}>Severity</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {allSeverities.map((s) => (
            <button key={s} onClick={() => onToggleSeverity(s)} style={chipStyle(selectedSeverities.has(s), SEVERITY_COLORS[s])}>
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export interface ActivityStreamProps {
  maxHeight?: string;
  /** When true, stream fills available height in a flex container */
  fillHeight?: boolean;
}

export const ActivityStream: React.FC<ActivityStreamProps> = ({ maxHeight = '500px', fillHeight = false }) => {
  const [showFilter, setShowFilter] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<Set<ActivityType>>(new Set());
  const [selectedSeverities, setSelectedSeverities] = useState<Set<ActivitySeverity>>(new Set());

  const wsFilter = useMemo(() => {
    const f: { types: ActivityType[]; severities: ActivitySeverity[] } = {
      types: [],
      severities: [],
    };
    if (selectedTypes.size > 0) f.types = Array.from(selectedTypes);
    if (selectedSeverities.size > 0) f.severities = Array.from(selectedSeverities);
    return f.types.length > 0 || f.severities.length > 0 ? f : undefined;
  }, [selectedTypes, selectedSeverities]);

  const {
    items,
    connectionStatus,
    isPaused,
    pause,
    resume,
    acknowledge,
  } = useActivityStream({ filter: wsFilter });

  // Client-side filter (for items already received before filter change)
  const filteredItems = useMemo(() => {
    let result = items;
    if (selectedTypes.size > 0) {
      result = result.filter((i) => selectedTypes.has(i.type));
    }
    if (selectedSeverities.size > 0) {
      result = result.filter((i) => selectedSeverities.has(i.severity));
    }
    return result;
  }, [items, selectedTypes, selectedSeverities]);

  const grouped = useMemo(() => groupByTime(filteredItems), [filteredItems]);

  const toggleType = useCallback((type: ActivityType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const toggleSeverity = useCallback((severity: ActivitySeverity) => {
    setSelectedSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(severity)) next.delete(severity);
      else next.add(severity);
      return next;
    });
  }, []);

  const activeFilterCount = selectedTypes.size + selectedSeverities.size;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ActivityIcon size={16} style={{ color: 'rgba(255,255,255,0.5)' }} />
          <span style={{ fontWeight: 600, fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
            Activity Stream
          </span>
          <span
            style={{
              fontSize: '11px',
              color: STATUS_COLORS[connectionStatus],
              marginLeft: '4px',
            }}
          >
            {STATUS_LABELS[connectionStatus]}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <button
            onClick={() => setShowFilter((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              fontSize: '11px',
              background: showFilter ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px',
              color: showFilter ? 'rgba(96,165,250,0.9)' : 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
            }}
          >
            <FilterIcon size={12} />
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>

          <button
            onClick={isPaused ? resume : pause}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              fontSize: '11px',
              background: isPaused ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px',
              color: isPaused ? 'rgba(251,191,36,0.9)' : 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
            }}
          >
            {isPaused ? <PlayIcon size={12} /> : <PauseIcon size={12} />}
            {isPaused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilter && (
        <FilterPanel
          selectedTypes={selectedTypes}
          selectedSeverities={selectedSeverities}
          onToggleType={toggleType}
          onToggleSeverity={toggleSeverity}
          onClose={() => setShowFilter(false)}
        />
      )}

      {/* Activity List */}
      <div style={{ overflowY: 'auto', padding: '8px', ...(fillHeight ? { flex: 1, minHeight: 0 } : { maxHeight }) }}>
        {filteredItems.length === 0 ? (
          <div
            style={{
              padding: '32px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.3)',
              fontSize: '13px',
            }}
          >
            {connectionStatus === 'connected'
              ? 'No activities yet. Events will appear here in real time.'
              : 'Connecting to activity stream...'}
          </div>
        ) : (
          Array.from(grouped.entries()).map(([groupLabel, groupItems]) => (
            <div key={groupLabel} style={{ marginBottom: '8px' }}>
              <div
                style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.35)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: '6px 12px 4px',
                }}
              >
                {groupLabel}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {groupItems.map((item) => (
                  <ActivityItemRow key={item.id} item={item} onAcknowledge={acknowledge} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paused Banner */}
      {isPaused && (
        <div
          style={{
            padding: '6px 12px',
            background: 'rgba(251,191,36,0.08)',
            borderTop: '1px solid rgba(251,191,36,0.15)',
            textAlign: 'center',
            fontSize: '11px',
            color: 'rgba(251,191,36,0.8)',
          }}
        >
          Stream paused — new events will not appear until resumed
        </div>
      )}
    </div>
  );
};

export default ActivityStream;
